import React, { useMemo } from 'react';
import { MemoryItem, CoupleSettings, VisitorInfo } from '../types';
import { MemoryCard } from './MemoryCard';
import {
  Sparkles,
  Camera,
} from 'lucide-react';

interface MediaFeedProps {
  memories: MemoryItem[];
  settings: CoupleSettings;
  currentVisitor?: VisitorInfo | null;
  activeAdminProfile?: 'novio' | 'novia';
  onOpenLightbox: (memory: MemoryItem, initialIndex?: number) => void;
  onGoToGarden?: () => void;
}

export type AlbumSortOrder = 'newest' | 'oldest';
export type AlbumLayoutMode = 'grid' | 'masonry';

export const MediaFeed: React.FC<MediaFeedProps> = ({
  memories,
  settings,
  currentVisitor,
  activeAdminProfile,
  onOpenLightbox,
}) => {
  const layoutMode: AlbumLayoutMode = 'grid';

  // Sort memories chronologically (newest first)
  const processedMemories = useMemo(() => {
    const result = [...memories];
    result.sort((a, b) => {
      const timeA = new Date(a.date || a.createdAt || 0).getTime();
      const timeB = new Date(b.date || b.createdAt || 0).getTime();
      return timeB - timeA;
    });
    return result;
  }, [memories]);

  return (
    <section
      className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-3 sm:pt-6 pb-20 touch-pan-y text-stone-900"
      id="galeria-view-container"
    >
      {/* ======================================================== */}
      {/* 1. HERO EXHIBITION BANNER */}
      {/* ======================================================== */}
      <div className="relative rounded-[2.5rem] overflow-hidden p-6 sm:p-10 mb-8 border border-stone-200/80 bg-gradient-to-br from-white via-[#FAF7F2] to-[#F5EFE6] shadow-[0_20px_50px_-15px_rgba(0,0,0,0.05)]">
        {/* Subtle decorative radial lights */}
        <div className="pointer-events-none absolute -top-24 -right-24 w-80 h-80 rounded-full bg-rose-200/35 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 -left-24 w-72 h-72 rounded-full bg-amber-100/40 blur-3xl" />

        <div className="relative z-10 flex flex-col justify-between gap-4">
          <div className="max-w-2xl">
            {/* Minimal Monogram Tag */}
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/90 border border-stone-200/80 text-rose-800 text-[11px] font-bold tracking-wider uppercase shadow-2xs mb-4">
              <Sparkles className="w-3.5 h-3.5 text-rose-500 animate-pulse" />
              <span>Galería</span>
            </div>

            {/* Haute Display Headline */}
            <h1 className="font-['Playfair_Display',serif] text-3xl sm:text-4xl lg:text-5xl font-extrabold text-stone-900 tracking-tight leading-tight">
              Nuestra Galería de Recuerdos
            </h1>

            {/* Poetic Subtitle */}
            <p className="text-sm sm:text-base text-stone-600 font-medium mt-3 leading-relaxed max-w-xl">
              Un viaje fotográfico por cada aventura, risa compartida y mirada de complicidad que da vida a nuestra historia de amor.
            </p>
          </div>
        </div>
      </div>

      {/* ======================================================== */}
      {/* 2. MEMORIES GALLERY GRID */}
      {/* ======================================================== */}
      {processedMemories.length > 0 ? (
        <div
          className="grid gap-6 sm:gap-8 items-start grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
        >
          {processedMemories.map((memory) => (
            <div key={memory.id} className="content-auto hardware-accelerated">
              <MemoryCard
                memory={memory}
                currentVisitor={currentVisitor}
                partner1Name={settings.partner1Name}
                partner2Name={settings.partner2Name}
                activeAdminProfile={activeAdminProfile}
                layoutMode={layoutMode}
                onOpenMediaLightbox={onOpenLightbox}
              />
            </div>
          ))}
        </div>
      ) : (
        /* Empty State */
        <div className="text-center py-20 px-6 bg-white/90 rounded-3xl border border-stone-200/80 flex flex-col items-center max-w-lg mx-auto shadow-2xs">
          <div className="w-16 h-16 bg-rose-50 rounded-2xl flex items-center justify-center text-rose-500 mb-4 shadow-2xs">
            <Camera className="w-8 h-8" />
          </div>
          <h3 className="font-['Playfair_Display',serif] text-xl font-bold text-stone-800">
            Aún no hay recuerdos publicados
          </h3>
          <p className="text-xs sm:text-sm text-stone-500 mt-2 leading-relaxed max-w-sm">
            Puedes añadir nuevas memorias y fotografías desde el panel de administración.
          </p>
        </div>
      )}
    </section>
  );
};
